import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import NodeDataStore from "csi/datastore/NodeDataStore";
import { C8Node, IDataStore } from "csi/typings";
import React, { useState } from "react";
import { ComponentItemDataStore } from "designSystem/utilities/component-item-functions";
import { ComponentItem } from "designSystem/types/component-item-types";
import DataGrid, { DataGridCardProps, DataGridProps } from "../dataGrid/DataGrid";
import FlexColumn, { FlexColumnProps } from "../flex/FlexColumn";
import SelectListItem, { SelectListItemProps } from "../list/SelectListItem";
import { ILocaleProvider, useLocale } from "designSystem/locale";
import SearchPlaceholder from "../placeholder/SearchPlaceholder";
import SearchTextField from "../textfield/SearchTextField";

export interface SearchSingleSelectProps<T> extends Omit<FlexColumnProps, "onSelect"> {
	/** properties to apply to the data grid containing the results */
	dataGridProps: Omit<DataGridProps<T>, "columns" | "columnsByWidth">;

	/** callback to determine whether or not should disable this item */
	getDisableItem?: (item: T) => boolean;

	/** indicates if component is in popup in which case TAB key is treated as ESC key */
	inPopup?: boolean;

	/** properties to apply to each list item */
	listItemProps?: SelectListItemProps;

	/** callback to convert full store into filtered store by search text */
	onSearchStore: (store: IDataStore<T> | NodeDataStore, searchText: string, loc: ILocaleProvider) => Promise<IDataStore<T> | NodeDataStore>;

	/** callback called when an item is selected or when blurred/escaped with no value */
	onSelect: (value?: string) => void;

	/** selected value */
	value?: string;
}

/** search callback for ComponentItemDataStore */
export async function onSearchComponentItemDataStore<T extends ComponentItem>(store: ComponentItemDataStore<T>, searchText: string, loc: ILocaleProvider): Promise<ComponentItemDataStore<T>> {
	return store.search(searchText, loc);
}

const SearchSingleSelect = <T extends ComponentItem | C8Node>(props: SearchSingleSelectProps<T>) => {
	const {
		dataGridProps,
		getDisableItem,
		inPopup,
		listItemProps,
		onSearchStore,
		onSelect,
		value,
		...otherProps
	} = props;

	const {
		CardComponent,
		PlaceholderComponent,
		rowsPerPage,
		store,
		...otherGridProps
	} = dataGridProps;

	const [searchStore, setSearchStore] = useState<IDataStore<T>>(store);
	const [searchText, setSearchText] = useState("");
	const [selectedValue, setSelectedValue] = useState(value);

	const loc = useLocale();

	const onSearchText = async (text: string) => {
		setSearchText(text);
		const filteredStore = await onSearchStore(store, text, loc);
		setSearchStore(filteredStore as IDataStore<T>);
	};

	const ItemComponent = (props: DataGridCardProps<T>) => {
		const { id, item } = props;
		const disabled = item.disabled || getDisableItem?.(item);

		return (
			<SelectListItem
				disabled={disabled}
				onClick={() => onSelect(id)}
				selected={selectedValue === id}
				{...listItemProps}
			>
				<CardComponent {...props} />
			</SelectListItem>
		);
	};

	function onEnter() {
		onSelect(selectedValue);
	}

	function onEscape() {
		onSelect();
	}

	function clickAway() {
		onSelect(selectedValue);
	}

	function onNext(previous: boolean) {
		const nextValue = searchStore.getNextIdentity(selectedValue, previous, true);
		if (nextValue) {
			setSelectedValue(nextValue);
		}
	}

	return (
		<ClickAwayListener onClickAway={clickAway}>
			<FlexColumn {...otherProps}>
				<Box p={2}>
					<SearchTextField
						autoFocus
						fullWidth
						handleTabAsEscape={inPopup}
						onChange={(event) => onSearchText(event.target.value)}
						onDown={() => onNext(false)}
						onEnter={onEnter}
						onEscape={onEscape}
						onUp={() => onNext(true)}
						placeholderRes="Search"
						value={searchText}
					/>
				</Box>
				<Box flexGrow={1} overflow="auto">
					<DataGrid
						CardComponent={ItemComponent}
						PlaceholderComponent={PlaceholderComponent || SearchPlaceholder}
						columns={1}
						rowsPerPage={rowsPerPage || 20}
						store={searchStore || store}
						{...otherGridProps}
					/>
				</Box>
			</FlexColumn>
		</ClickAwayListener>
	);
};

export default SearchSingleSelect;